import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  ReactNode,
} from 'react'
import type { Map } from 'mapbox-gl'
import { useRouter } from '@popety-io/ui'
import type { BasemapStyle, ViewMode } from '../types/lands.types'
import type { LayerType } from '../../types'
import type { Land } from '@popety-io/types'
import { layerStorage } from '../utils/localStorage.util'

export interface MapContextValue {
  // Map state
  map: Map | null
  mapLoaded: boolean
  basemap: BasemapStyle
  activeLayer: LayerType | null
  transparency: number
  favoriteLayers: LayerType[]

  // View state
  viewMode: ViewMode

  // Panel state
  layersPanelOpen: boolean
  legendPanelOpen: boolean
  filterPanelOpen: boolean
  propertyPanelOpen: boolean
  selectedPropertyId: string | null
  selectedLandData: Land | null

  // Map actions
  setMap: (map: Map | null) => void
  setMapLoaded: (loaded: boolean) => void
  setBasemap: (style: BasemapStyle) => void
  setActiveLayer: (layer: LayerType | null) => void
  setTransparency: (value: number) => void
  toggleFavoriteLayer: (layerId: LayerType) => void

  // View actions
  setViewMode: (mode: ViewMode) => void

  // Panel actions
  setLayersPanelOpen: (open: boolean) => void
  setLegendPanelOpen: (open: boolean) => void
  setFilterPanelOpen: (open: boolean) => void
  setPropertyPanelOpen: (open: boolean) => void
  setSelectedPropertyId: (id: string | null) => void
  setSelectedLandData: (land: Land | null) => void
  openPropertyPanel: (propertyId: string) => void
  closePropertyPanel: () => void
}

const MapContext = createContext<MapContextValue | undefined>(undefined)

interface MapProviderProps {
  children: ReactNode
}

export const MapProvider: React.FC<MapProviderProps> = ({ children }) => {
  const { query, updateQuery } = useRouter()
  const { basemap: basemapQuery, view: viewQuery } = query

  // Initialize state from localStorage and URL
  const [map, setMap] = useState<Map | null>(null)
  const [mapLoaded, setMapLoaded] = useState(false)
  const [basemap, setBasemapState] = useState<BasemapStyle>(
    (basemapQuery as BasemapStyle) || 'street'
  )
  const [viewMode, setViewModeState] = useState<ViewMode>(() => {
    // Priority: URL query > localStorage > default
    if (viewQuery && (viewQuery === 'map' || viewQuery === 'list')) {
      return viewQuery as ViewMode
    }
    return layerStorage.getViewMode()
  })
  const [activeLayer, setActiveLayerState] = useState<LayerType | null>(() =>
    layerStorage.getActiveLayer()
  )
  const [transparency, setTransparencyState] = useState(() =>
    layerStorage.getTransparency()
  )
  const [layersPanelOpen, setLayersPanelOpen] = useState(false)
  const [legendPanelOpen, setLegendPanelOpen] = useState(false)
  const [filterPanelOpen, setFilterPanelOpen] = useState(true)
  const [propertyPanelOpen, setPropertyPanelOpen] = useState(false)
  const [selectedPropertyId, setSelectedPropertyId] = useState<string | null>(
    null
  )
  const [selectedLandData, setSelectedLandData] = useState<Land | null>(null)
  const [favoriteLayers, setFavoriteLayers] = useState<LayerType[]>(() => {
    const stored = layerStorage.getFavoriteLayers()
    return stored.length > 0
      ? stored
      : [
          'development_score' as LayerType,
          'restriction_type' as LayerType,
          'day_noise_street' as LayerType,
        ]
  })

  // Wrapped setters that also update localStorage/URL
  const setBasemap = useCallback(
    (style: BasemapStyle) => {
      setBasemapState(style)
      updateQuery({ basemap: style })
    },
    [updateQuery]
  )

  const setActiveLayer = useCallback((layer: LayerType | null) => {
    setActiveLayerState(layer)
    layerStorage.setActiveLayer(layer)
  }, [])

  const setTransparency = useCallback((value: number) => {
    setTransparencyState(value)
    layerStorage.setTransparency(value)
  }, [])

  const setViewMode = useCallback(
    (mode: ViewMode) => {
      setViewModeState(mode)
      layerStorage.setViewMode(mode)
      updateQuery({ view: mode })
    },
    [updateQuery]
  )

  const toggleFavoriteLayer = useCallback((layerId: LayerType) => {
    setFavoriteLayers((prev) => {
      const updated = prev.includes(layerId)
        ? prev.filter((id) => id !== layerId)
        : [...prev, layerId]
      layerStorage.setFavoriteLayers(updated)
      return updated
    })
  }, [])

  // Property panel functions
  const openPropertyPanel = useCallback((propertyId: string) => {
    setSelectedPropertyId(propertyId)
    setPropertyPanelOpen(true)
  }, [])

  const closePropertyPanel = useCallback(() => {
    setPropertyPanelOpen(false)
    setSelectedPropertyId(null)
    setSelectedLandData(null)

    // Remove landId from URL while preserving other parameters
    const newQuery = { ...query }
    delete newQuery.landId
    updateQuery(newQuery)
  }, [updateQuery, query])

  const value: MapContextValue = {
    map,
    mapLoaded,
    basemap,
    activeLayer,
    transparency,
    viewMode,
    layersPanelOpen,
    legendPanelOpen,
    filterPanelOpen,
    propertyPanelOpen,
    selectedPropertyId,
    selectedLandData,
    favoriteLayers,
    setMap,
    setMapLoaded,
    setBasemap,
    setActiveLayer,
    setTransparency,
    setViewMode,
    setLayersPanelOpen,
    setLegendPanelOpen,
    setFilterPanelOpen,
    setPropertyPanelOpen,
    setSelectedPropertyId,
    setSelectedLandData,
    openPropertyPanel,
    closePropertyPanel,
    toggleFavoriteLayer,
  }

  return <MapContext.Provider value={value}>{children}</MapContext.Provider>
}

export const useMap = () => {
  const context = useContext(MapContext)
  if (!context) {
    throw new Error('useMap must be used within a MapProvider')
  }
  return context
}
